import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import authServiceClient from "../services/Application/authServiceClient";
import { CSpinner, CContainer, CRow, CCol, CAlert } from "@coreui/react";

const LoginCallback = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processed, setProcessed] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Prevent multiple executions
    if (processed) {
      return;
    }
    const handleCallback = async () => {
      try {
        setProcessed(true); // Mark as processed to prevent re-execution

        // Try multiple ways to get URL parameters
        let urlParams = new URLSearchParams(location.search);

        // If location.search is empty, try window.location.search
        if (!location.search && window.location.search) {
          urlParams = new URLSearchParams(window.location.search);
        }

        const code = urlParams.get("code");
        const state = urlParams.get("state");
        const error = urlParams.get("error");

        if (error) {
          throw new Error(`Authentication error: ${error}`);
        }

        if (!code) {
          throw new Error("No authorization code received");
        }

        // Extract provider from state or URL path
        let provider = "microsoft"; // default
        if (state) {
          try {
            const stateData = JSON.parse(atob(state));
            provider = stateData.provider || "microsoft";
          } catch (e) {
            // Could not parse state parameter, using default provider
          }
        }

        const oAuthData = {
          AuthorizationCode: code,
          provider,
          redirectUri: `${window.location.origin}/auth/callback`,
        };

        let response = await authServiceClient.signInWithOAuth(oAuthData);
        if (response && response.succeeded) {
          // Call onLoginSuccess and redirect to home
          if (onLoginSuccess) {
            await onLoginSuccess(response.object);
          }

          // If the account requires a password change, go there immediately
          if (response.object?.user?.forcePasswordChange) {
            window.location.replace(
              window.location.origin + "/#/forgot-password",
            );
            return;
          }

          // Use replace to prevent back navigation to callback
          const returnUrl = sessionStorage.getItem("returnUrl");
          if (returnUrl) {
            sessionStorage.removeItem("returnUrl");
            window.location.replace(window.location.origin + "/#" + returnUrl);
          } else {
            window.location.replace(window.location.origin + "/#/dashboard");
          }
        } else {
          const errorMsg =
            response?.errorMessage || response?.message || "OAuth login failed";
          throw new Error(errorMsg);
        }
      } catch (error) {
        setError(error.message);
        toast.error("Login failed. Please try again.");

        // Redirect to login page after error
        setTimeout(() => {
          window.location.replace(window.location.origin + "/#/login");
        }, 3000);
      } finally {
        setLoading(false);
      }
    };
    handleCallback();
  }, [location, onLoginSuccess, processed]);

  if (loading) {
    return (
      <div className="c-app c-default-layout flex-row align-items-center">
        <CContainer>
          <CRow className="justify-content-center">
            <CCol md="6" className="text-center">
              <CSpinner color="primary" size="lg" />
              <p className="mt-3">Processing login...</p>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    );
  }

  if (error) {
    return (
      <div className="c-app c-default-layout flex-row align-items-center">
        <CContainer>
          <CRow className="justify-content-center">
            <CCol md="6">
              <CAlert color="danger">
                <h4>Login Failed</h4>
                <p>{error}</p>
                <p>Redirecting to login page...</p>
              </CAlert>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    );
  }

  return null;
};

export default LoginCallback;
